import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Ho4ArticleSidebar from '../components/Ho4ArticleSidebar';
import { HO4_LAST_MODIFIED } from '../data/ho4Articles';

function Ho4AcvVsReplacementCostArticlePage() {
  const navigate = useNavigate();
  const articleLinkStyle = { color: '#065F46', fontWeight: 600, textDecoration: 'underline' };

  useEffect(() => {
    const previousTitle = document.title;
    const descriptionMeta = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionMeta?.getAttribute('content') ?? '';

    document.title = 'Actual Cash Value vs. Replacement Cost for Renters Insurance | GetSkimp';

    if (descriptionMeta) {
      descriptionMeta.setAttribute(
        'content',
        'Compare actual cash value (ACV) and replacement cost coverage on renters insurance. See how each affects claim payouts, premiums, and which option fits your belongings.'
      );
    }

    return () => {
      document.title = previousTitle;
      if (descriptionMeta) descriptionMeta.setAttribute('content', previousDescription);
    };
  }, []);

  return (
    <div className="container">
      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: '2rem' }}>
        <Ho4ArticleSidebar
          activeHref="/knowledge-base/actual-cash-value-vs-replacement-cost-renters-insurance"
          onNavigate={(href) => navigate(href)}
        />
        <section>
          <div style={{ marginBottom: '1rem' }}>
            <button type="button" className="btn btn-sm btn-outline" onClick={() => navigate('/knowledge-base')} style={{ marginBottom: '1rem' }}>
              Back to self help library
            </button>
            <h2 style={{ marginBottom: '0.5rem' }}>Actual cash value vs. replacement cost for renters insurance</h2>
            <p style={{ marginBottom: 0, color: '#4B5563' }}>
              The settlement method on your HO-4 policy determines how much you receive when belongings are damaged or
              stolen. This choice affects every personal property claim you file.
            </p>
            <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#6B7280' }}>Last modified: {HO4_LAST_MODIFIED}</p>
          </div>

          <article className="card mb-4">
            <div className="card-body">
              <h3 style={{ marginBottom: '0.75rem' }}>Quick answer</h3>
              <p>
                <strong>Replacement cost coverage (RCV)</strong> pays to replace damaged items with new ones of similar
                kind and quality, up to your policy limit. <strong>Actual cash value (ACV)</strong> pays replacement cost
                minus depreciation — so older items receive lower payouts. Most renters benefit from replacement cost
                coverage because the premium difference is often small relative to the claim payout gap.
              </p>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Side-by-side comparison</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Factor</th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Replacement cost (RCV)</th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Actual cash value (ACV)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>How payout is calculated</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Cost to buy new today</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>New cost minus age/wear depreciation</td>
                    </tr>
                    <tr>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Typical premium</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Slightly higher</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Lower</td>
                    </tr>
                    <tr>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Best for</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Most renters with everyday belongings</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Budget-first policies with minimal belongings</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '0.6rem' }}>Claim experience</td>
                      <td style={{ padding: '0.6rem' }}>Easier to fully replace what you lost</td>
                      <td style={{ padding: '0.6rem' }}>May leave a gap you pay out of pocket</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Example: 4-year-old laptop stolen</h3>
              <p>
                Suppose a laptop you bought for $1,200 four years ago would cost $1,100 to replace today. Under
                replacement cost coverage, your insurer may pay up to $1,100 (minus deductible). Under actual cash value,
                depreciation might reduce the payout to $600–$800 depending on the insurer&apos;s calculation — leaving
                you to cover the rest.
              </p>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Which should you choose?</h3>
              <ul style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>Choose replacement cost</strong> if you want to actually replace furniture, electronics, and
                  clothing after a loss without a large out-of-pocket gap.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>Consider ACV</strong> only if premium savings matter more than claim payout, and you could
                  absorb a depreciation gap yourself.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  Match your settlement type with an appropriate{' '}
                  <Link to="/knowledge-base/how-much-personal-property-coverage-do-i-need-for-renters-insurance" style={articleLinkStyle}>personal property limit</Link>.
                </li>
                <li>
                  Ask whether your policy pays replacement cost upfront or pays ACV first and reimburses the difference
                  after you actually replace the item — both are common.
                </li>
              </ul>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Related decisions when buying HO-4</h3>
              <p style={{ marginBottom: 0 }}>
                Settlement type works alongside your{' '}
                <Link to="/knowledge-base/what-deductible-should-i-choose-for-ho4" style={articleLinkStyle}>deductible</Link>,{' '}
                <Link to="/knowledge-base/how-much-liability-do-i-need-for-renters-insurance" style={articleLinkStyle}>liability limit</Link>, and overall premium — see{' '}
                <Link to="/knowledge-base/how-much-does-ho4-cost-in-texas" style={articleLinkStyle}>typical HO-4 costs in Texas</Link>{' '}
                for context. Ready to buy? Follow{' '}
                <Link to="/knowledge-base/how-to-get-ho4-renters-insurance" style={articleLinkStyle}>how to get HO-4</Link>.
              </p>
            </div>
          </article>

          <section className="card mb-4" style={{ borderColor: 'var(--color-green)', background: 'linear-gradient(135deg, #ECFDF5 0%, #FFFFFF 100%)', boxShadow: 'var(--shadow-md)' }}>
            <div className="card-body">
              <h3 style={{ marginBottom: '0.5rem', color: '#065F46' }}>Start renters insurance with GetSkimp</h3>
              <p style={{ marginBottom: '1rem' }}>Compare HO-4 options and choose the settlement type that protects your belongings best.</p>
              <button type="button" className="btn btn-primary" onClick={() => navigate('/qualify')} style={{ fontWeight: 700, paddingInline: '1.4rem' }}>Get started now</button>
            </div>
          </section>
          <section className="card" style={{ borderColor: '#FBBF24', background: 'linear-gradient(135deg, #FFFBEB 0%, #FFFFFF 100%)' }}>
            <div className="card-body">
              <h3 style={{ marginBottom: '0.5rem', color: '#92400E' }}>Want help comparing ACV and RCV?</h3>
              <p style={{ marginBottom: '1rem' }}>An agent can walk through how each option affects your specific policy quote.</p>
              <button type="button" className="btn btn-outline" onClick={() => navigate('/help')} style={{ borderColor: '#D97706', color: '#92400E', fontWeight: 700 }}>Speak to an agent</button>
            </div>
          </section>
        </section>
      </div>
    </div>
  );
}

export default Ho4AcvVsReplacementCostArticlePage;
